import { useEffect, useRef, useState } from 'react'
import { IconX, IconChevronRight, IconDownload } from '../icons'

export interface Shot {
  /** 原图地址：点开之后看的是这张。 */
  src: string
  /** 缩略图地址。先顶上，原图到了再换，免得点开先对着一块空白等。 */
  thumb: string
  /** 这张图是什么（「诊断对照板」「叠瓦热力图」…）。 */
  label: string
  /** 它出自哪一步（「第 1 步 · 诊断」）。同一趟里同名的图有好几张，靠这个分辨。 */
  from?: string
}

/**
 * 灯箱：把轨迹里那些小预览放大到整屏看。
 *
 * 背景：2026-09-11 用户裁决——「这里图片最好能直接看到预览，点击还能放大到灯箱」。
 *   在此之前每一步写下的图只是一行文字链接，点开是新标签页里的一张裸图，
 *   看完还得切回来；而这些图（对照板、热力图、参考本）本来就是要跟上下文一起读的。
 * 设计意图：一趟作业的图当成一组，左右键可以来回翻——同一种图在重画前后各有一张，
 *   要看出区别就得来回比，回退到列表再点开第二张的话，那个区别就没了。
 * 关键约束：
 *   1. 原图是几兆的 PNG，先铺缩略图（模糊拉伸）再换原图。空屏加转圈会让人
 *      以为点坏了，而这一档的图恰恰是「点开才有意义」的东西。
 *   2. Esc 关、点背景关、关掉之后焦点回到刚才那颗缩略图上——键盘走到这儿不能掉链子。
 *   3. 开着的时候锁住页面滚动：底下那一长条轨迹会在灯箱背后跟着滚，关掉之后
 *      人就找不回原来看到哪儿了。
 */
export function Lightbox({ shots, index, onClose }: {
  shots: Shot[]
  index: number
  onClose: () => void
}) {
  const [at, setAt] = useState(index)
  const [full, setFull] = useState(false)
  const closeRef = useRef<HTMLButtonElement>(null)
  const shot = shots[at]

  useEffect(() => setAt(index), [index])
  useEffect(() => setFull(false), [at])

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') setAt(i => (i + 1) % shots.length)
      else if (e.key === 'ArrowLeft') setAt(i => (i - 1 + shots.length) % shots.length)
    }
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('keydown', key)
      document.body.style.overflow = prev
      opener?.focus?.()
    }
  }, [shots.length, onClose])

  if (!shot) return null

  return (
    <div className="lb" role="dialog" aria-modal="true" aria-label={shot.label}
         onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="lb-bar">
        <div className="lb-title">
          <b>{shot.label}</b>
          {shot.from && <span>{shot.from}</span>}
          {shots.length > 1 && <span className="lb-n">{at + 1} / {shots.length}</span>}
        </div>
        <a className="lb-btn" href={shot.src} target="_blank" rel="noreferrer"
           title="在新标签页打开原图" onClick={e => e.stopPropagation()}>
          <IconDownload size={16} />原图
        </a>
        <button ref={closeRef} className="lb-btn" onClick={onClose} title="关闭（Esc）">
          <IconX size={16} />
        </button>
      </div>

      <div className="lb-stage" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
        {shots.length > 1 && (
          <button className="lb-nav is-prev" title="上一张（←）"
                  onClick={() => setAt(i => (i - 1 + shots.length) % shots.length)}>
            <IconChevronRight size={20} />
          </button>
        )}
        {/* 缩略图垫在下面，原图加载完再盖上去。两张同一个 object-fit，换的时候不会跳。 */}
        <img className="lb-img is-thumb" src={shot.thumb} alt="" aria-hidden
             style={{ opacity: full ? 0 : 1 }} />
        <img className="lb-img" src={shot.src} alt={shot.label}
             style={{ opacity: full ? 1 : 0 }} onLoad={() => setFull(true)} />
        {shots.length > 1 && (
          <button className="lb-nav is-next" title="下一张（→）"
                  onClick={() => setAt(i => (i + 1) % shots.length)}>
            <IconChevronRight size={20} />
          </button>
        )}
      </div>
    </div>
  )
}
